/**
 * Hook for Access Policy Management
 *
 * Provides a React hook for initializing and managing access policies
 * with automatic policy loading and configuration.
 */
import { useEffect, useState } from 'react';
import AccessPolicyService from '../services/AccessPolicyService';
import { defaultAccessPolicyRegistry } from '../config/accessPolicies';
import { AccessPolicyRegistry, PolicyConfiguration } from '../types/accessControl';
import { logger } from "../utils/logger";
// import { logger } from "../utils/logger";
interface UseAccessPoliciesOptions {
    /** Custom policy registry (overrides default) */
    customRegistry?: AccessPolicyRegistry;
    /** Policy configuration */
    configuration?: Partial<PolicyConfiguration>;
    /** Whether to auto-initialize on mount */
    autoInitialize?: boolean;
}
interface UseAccessPoliciesReturn {
    /** Whether policies are initialized */
    isInitialized: boolean;
    /** Initialize policies manually */
    initialize: (registry?: AccessPolicyRegistry, config?: Partial<PolicyConfiguration>) => void;
    /** Clear policy cache */
    clearCache: () => void;
    /** Get cache statistics */
    getCacheStats: () => {
        size: number;
        hitRate: number;
    };
}
/**
 * Hook for managing access policies
 * @param options - Configuration options
 * @returns Policy management functions and state
 */
export const useAccessPolicies = (options: UseAccessPoliciesOptions = {}): UseAccessPoliciesReturn => {
    const { customRegistry, configuration, autoInitialize = true } = options;
    const [isInitialized, setIsInitialized] = useState(false);
    const initialize = (registry?: AccessPolicyRegistry, config?: Partial<PolicyConfiguration>) => {
        try {
            const registryToUse = registry || customRegistry || defaultAccessPolicyRegistry;
            const configToUse = { ...configuration, ...config };
            AccessPolicyService.initialize(registryToUse, configToUse);
            setIsInitialized(true);
            logger.debug('[useAccessPolicies] Policies initialized successfully');
        }
        catch (error) {
            logger.error('[useAccessPolicies] Failed to initialize policies:', error, {});
            setIsInitialized(false);
        }
    };
    const clearCache = () => {
        AccessPolicyService.clearCache();
    };
    const getCacheStats = () => {
        return AccessPolicyService.getCacheStats();
    };
    // Auto-initialize on mount if enabled
    useEffect(() => {
        if (autoInitialize && !isInitialized) {
            initialize();
        }
    }, [autoInitialize, isInitialized]);
    return {
        isInitialized,
        initialize,
        clearCache,
        getCacheStats
    };
};
export default useAccessPolicies;
